<template>
  <InView
    v-slot="{ inView }"
    :style="{ background: color }"
  >
    <ScreenWidthProvider
      v-slot="{ isNarrower, isWider }"
      class="product-itinerary__item_content h-full flex items-center"
    >
      <div class="w-full">
        <div class="px-6 px-print-0 py-4 clearfix">
          <div
            v-if="filteredMedia.length > 0 && isWider('md', 1)"
            class="product-itinerary__item_media float-right print-hidden"
          >
            <div
              v-if="!inView"
              class="product-itinerary__item_media_item w-full hidden md:flex"
            >
              <b-skeleton-img class="size-full" />
            </div>
            <div
              v-else-if="isMediaCarousel === true"
              class="product-itinerary__item_media_item w-full hidden md:flex"
            >
              <LazyMediaCarousel
                class="size-full"
                :slides="mediaSlides"
                v-bind="mediaCarouselOptions"
              />
            </div>
            <template v-else-if="isMediaCarousel === false">
              <div
                v-for="(media, index) in filteredMedia"
                :key="index"
                class="product-itinerary__item_media_item mb-2 hidden md:flex overflow-hidden content-center"
                :class="{ 'mb-2': filteredMedia.length > 1, 'cursor-zoom-in': allowFullscreen }"
                @click="$emit('showPreview', { currentIndex: index, mediaList: filteredMedia, title: itineraryDay.title })"
              >
                <div class="relative size-full">
                  <LazyResponsiveImage
                    v-if="media.type === 'cloudinary_image'"
                    class="size-full"
                    :image="media.location"
                    :alt="itineraryDay.title"
                    lazy
                    cover
                    loading="lazy"
                  />
                  <LazyMediaPlayer
                    v-if="media.type === 'youtube'"
                    class="h-full"
                    :url="media.location"
                    just-preview
                  />
                </div>
              </div>
            </template>
          </div>
          <div
            v-if="itineraryDay.day !== 'x' || itineraryDay.box_type === boxTypes.hotel"
            class="mb-4 product-itinerary__item-title"
            @mouseover="$emit('setMarker', itineraryDay.day)"
            @click="expandAll = true"
          >
            <span>
              <span
                v-if="hasDay"
                class="font-bold"
              >{{ $t('day') }} {{ itineraryDay.day }}</span>
              <span
                class="font-bold"
                :data-translation="itineraryDay?.title_translationkey"
                :class="{ 'ml-2 md:font-normal font-weight-print-normal': hasDay }"
              >{{ itineraryDay.title }}
                <div
                  v-if="itineraryDay.map_points.length > 0 && allowOpenMap"
                  class="itinerary-item-map-marker inline-block cursor-pointer mx-2"
                  @click="$emit('openMap', itineraryDay.day)"
                >
                  <fa
                    v-rtg-tooltip="$t('showOnMap')"
                    icon="map"
                  />
                </div>
              </span>
            </span>
          </div>
          <div
            class="itinerary-html"
            :data-translation="itineraryDay?.htmltext_translationkey"
            :class="textClass"
            v-html="itineraryDay.htmltext"
          />
          <div
            v-if="filteredMedia.length"
            ref="virtualTextContainer"
            class="absolute opacity-0 hidden pointer-events-none product-itinerary-virtual-text top-0 left-0 w-full itinerary-html"
            v-html="itineraryDay.htmltext"
          />
        </div>
        <div
          v-if="filteredMedia?.length && isNarrower('md')"
          class="product-itinerary__item_media_item flex md:hidden mt-4 p-0 print-hidden"
        >
          <div
            v-if="!inView"
            class="product-itinerary__item_media_item flex size-full"
          >
            <b-skeleton-img
              class="size-full"
              no-aspect
            />
          </div>
          <LazyMediaCarousel
            v-else
            class="size-full"
            :slides="mediaSlides"
            v-bind="mediaCarouselOptions"
          />
        </div>
      </div>
    </ScreenWidthProvider>
  </InView>
</template>

<script>
import { boxTypes } from '@packages/trip/constants/types.ts'
import colors from '#tailwind-config/theme/colors'

export default defineNuxtComponent({
  props: {
    itineraryDay: {
      type: Object,
      required: true,
    },

    isDestination: {
      type: Boolean,
      default: false,
    },

    color: {
      type: String,
      default: colors.gray[100],
    },

    allowFullscreen: {
      type: Boolean,
      default: true,
    },

    allowOpenMap: {
      type: Boolean,
      default: true,
    },

    sliderNavigation: {
      type: Boolean,
      default: true,
    },

    textClass: {
      type: String,
      default: '',
    },
  },

  emits: ['openMap', 'setMarker', 'showPreview'],

  data () {
    return {
      /**
       * Desktop: If false, display images alone or display images below each other
       * Mobile: Always show carousel
       */
      isMediaCarousel: null,
      boxTypes,
    }
  },

  computed: {
    mediaCarouselOptions () {
      return {
        allowFullscreen: this.allowFullscreen,
        constantDisplayNavigation: this.sliderNavigation,
        navigation: this.sliderNavigation,
        dotsAlign: 'bottom',
        arrowsType: 'circular',
        arrowsSize: 'sm',
        lazyCarouselImages: true,
        loadingSkeletonAspect: false,
        loop: true,
        modalProps: {
          centered: true,
          showClose: true,
          size: 'xl',
        },
      }
    },

    filteredMedia () {
      return (this.itineraryDay?.media || [])
        .filter(media => ['cloudinary_image', 'youtube'].includes(media.type))
        .slice(0, 4)
    },

    mediaSlides () {
      return this.filteredMedia.map(media => ({
        mediatype: media.type === 'cloudinary_image' ? 'image' : 'video',
        type: media.type === 'cloudinary_image' ? 'cloudinary' : media.type,
        url: media.location,
        title: this.itineraryDay.title,
        justPreview: true,
        autoplay: true,
      }))
    },

    hasDay () {
      return !this.isDestination && this.itineraryDay.day !== '*' && this.itineraryDay.day !== 'x'
    },
  },

  watch: {
    itineraryDay () {
      this.shouldMediaCarousel()
    },
  },

  beforeMount () {
    this.shouldMediaCarousel()
  },

  mounted () {
    // Timeout to prevent resize triggering on page load for unknown reason
    window.setTimeout(() => {
      if (!this._isDestroyed) {
        window.addEventListener('resize', this.shouldMediaCarousel)
      }
    }, 300)
  },

  beforeUnmount () {
    this.awaitShouldMediaCarouselAbortController?.abort()
    window.removeEventListener('resize', this.shouldMediaCarousel)
  },

  methods: {
    async shouldMediaCarousel () {
      if (typeof window === 'undefined' || !this.filteredMedia.length) {
        return
      }

      if (this.filteredMedia.length === 1) {
        this.isMediaCarousel = false
      } else if (this.filteredMedia.length > 2) {
        this.isMediaCarousel = true
      } else {
        this.awaitShouldMediaCarouselAbortController?.abort()
        this.awaitShouldMediaCarouselAbortController = new AbortController()

        try {
          await promiseUntilCondition(() => this.$refs.virtualTextContainer, {
            interval: 50,
            abortSignal: this.awaitShouldMediaCarouselAbortController?.signal,
          })

          if (!this.$refs.virtualTextContainer) {
            throw new Error('virtualTextContainer ref is not available')
          }

          const filteredMediaHeight = this.filteredMedia.length * 200
          this.isMediaCarousel = this.$refs.virtualTextContainer.offsetHeight < filteredMediaHeight - 100 // media starts at top
        } catch (error) {
          this.isMediaCarousel = false
        }
      }
    },
  },
})
</script>

<style lang="scss">
$product-itinerary-media-width: 300px;
$product-itinerary-media-left-padding: 1.1rem;

.product-itinerary {
  &__item {
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid theme('colors.gray.300');
    padding: 30px 30px 30px 0;

    @media (max-width: theme('screens.md')) {
      padding-bottom: 0;
    }

    &_media {
      padding-left: $product-itinerary-media-left-padding;
      padding-bottom: .1rem;
      width: $product-itinerary-media-width;

      @media (max-width: theme('screens.sm')) {
        width: 100%;
        padding-left: 0;
      }

      &_item {
        height: 200px;

        @media (max-width: theme('screens.md')) {
          height: 300px;
        }
      }
    }
  }

  &-virtual-text {
    pointer-events: none;
  }
}

.product-featured-itinerary {
  .itinerary-item {
    &-map {
      display: flex;
      padding: 1em;
      align-items: center;
      margin-bottom: 1em;
      background: theme('colors.gray.100');

      &:hover {
        background: theme('colors.gray.300');
      }

      &.nomap {
        cursor: initial;
      }
    }

    &-map-marker {
      width: 20px;
      height: 20px;
      min-width: 20px;
    }
  }
}

.itinerary-html {
  a {
    font-weight: bold;
  }
}
</style>
